import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { FeatureStore } from "services/feature/featureStore";
import { type PartnerOffersStore } from "services/partnerOffers/partnerOffersStore";
import { BasketQuestion } from "services/questions/gb/basketConstants";
import { type BasketQuestionsStore } from "services/questions/gb/basketQuestionsStore";
import type { CHVerificationQuestionStoreV2 } from "services/questions/gb/chVerificationQuestionStoreV2";
import { type CompanyQuestionsStore } from "services/questions/gb/companyQuestionsStore";
import { type DirectorQuestionsStore } from "services/questions/gb/directorQuestionsStore";
import { type IdentityQuestionsStore } from "services/questions/gb/identityQuestionsStore";
import type { InitQuestionStore } from "services/questions/gb/initQuestionStore";
import { type ParticularQuestionStore } from "services/questions/gb/particularsQuestionsStore";
import { type QuestionsStore } from "services/questions/questionsStore";
import { RegisterFormStep } from "services/registerForm/registerFormStep";
import { RegisterFormSteps } from "services/registerForm/registerFormSteps";
import { type DirectionType, RegisterFormStepsStoreBase } from "services/registerForm/registerFormStepsStoreBase";
import { IncoroporationFormStep } from "./constants";

export class IncorporationRegisterFormStepsStore extends RegisterFormStepsStoreBase<IncoroporationFormStep> {
  protected readonly _steps: RegisterFormSteps<IncoroporationFormStep>;
  private readonly _hasPartnerOffers: boolean;
  private readonly _stepAfterCheckout: IncoroporationFormStep;
  readonly confirmStep: IncoroporationFormStep = IncoroporationFormStep.CONFIRM;
  readonly completeStep: IncoroporationFormStep = IncoroporationFormStep.COMPLETE;

  constructor(
    private readonly _companyQuestionsStore: CompanyQuestionsStore,
    private readonly _directorQuestionsStore: DirectorQuestionsStore,
    private readonly _particularsQuestionsStore: ParticularQuestionStore,
    private readonly _identityQuestionsStore: IdentityQuestionsStore,
    private readonly _chVerificationQuestionStoreV2: CHVerificationQuestionStoreV2,
    private readonly _basketQuestionsStore: BasketQuestionsStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _initQuestionStore: InitQuestionStore,
    private readonly _featureStore: FeatureStore,
    private readonly _partnerOffersStore: PartnerOffersStore,
  ) {
    super(_analyticsManager);

    this._hasPartnerOffers =
      this._featureStore.hasFeature("formationPartnerOffers") && this._partnerOffersStore.hasOffers;

    const identityStep = new RegisterFormStep(
      IncoroporationFormStep.IDENTITY,
      "Confirm identity",
      this._identityQuestionsStore,
    );
    // Partner offers sit between payment and identity: the customer has paid, and referrals are added
    // before they move on to ID/POA.
    const partnerOffersSteps = this._hasPartnerOffers
      ? [new RegisterFormStep(IncoroporationFormStep.PARTNER_OFFERS, "Partner offers")]
      : [];
    const basketStep = new RegisterFormStep(IncoroporationFormStep.BASKET, "Your basket", this._basketQuestionsStore);
    const confirmStep = new RegisterFormStep(IncoroporationFormStep.CONFIRM, "Confirm and submit");
    const chargeStep = new RegisterFormStep(IncoroporationFormStep.CHARGE, "Payment details");

    const commonStartSteps = [
      new RegisterFormStep(IncoroporationFormStep.CONSENT, "Before you get started"),
      new RegisterFormStep(IncoroporationFormStep.BEFORE_START, "Before you get started", this._initQuestionStore),
      new RegisterFormStep(IncoroporationFormStep.DIRECTOR, "Personal Details", this._directorQuestionsStore),
      new RegisterFormStep(IncoroporationFormStep.COMPANY, "About your business", this._companyQuestionsStore),
      new RegisterFormStep(IncoroporationFormStep.SHARES, "Particulars", this._particularsQuestionsStore),
    ];

    const commonEndSteps = [
      new RegisterFormStep(
        IncoroporationFormStep.CH_VERIFICATION,
        "Companies House Verification",
        this._chVerificationQuestionStoreV2 as QuestionsStore<any>,
      ),
      // Final phone-call compliance gate before submission (deferred from CH_VERIFICATION): a call,
      // then a wait for the compliance check the call triggers.
      ...(this._featureStore.hasFeature("formationComplianceCall")
        ? [
            new RegisterFormStep(IncoroporationFormStep.COMPLIANCE_CALL, "Final check"),
            new RegisterFormStep(IncoroporationFormStep.COMPLIANCE_WAIT, "Final check"),
          ]
        : []),
      new RegisterFormStep(IncoroporationFormStep.COMPLETE, "Complete"),
    ];

    // Pay first, verify after
    const steps = [
      ...commonStartSteps,
      confirmStep,
      basketStep,
      chargeStep,
      ...partnerOffersSteps,
      identityStep,
      ...commonEndSteps,
    ];
    this._steps = new RegisterFormSteps<IncoroporationFormStep>(steps);

    const stepTypes = steps.map((step) => step.type);
    this._stepAfterCheckout =
      stepTypes[stepTypes.indexOf(IncoroporationFormStep.CHARGE) + 1] ?? IncoroporationFormStep.IDENTITY;
  }

  // The checkout branches in IncorporationRegisterFormStore jump straight past CHARGE, so they need
  // to know where the flow continues. Derived from the step order, so moving a step needs no change here.
  get stepAfterCheckout(): IncoroporationFormStep {
    return this._stepAfterCheckout;
  }

  override setStep(step: IncoroporationFormStep, direction?: DirectionType): void {
    // Guard: redirect CHARGE to BASKET when combined checkout is enabled
    if (this._featureStore.hasFeature("formationOnePageCheckout") && step === IncoroporationFormStep.CHARGE) {
      this._analyticsManager.event("combined-checkout.charge-step-guarded");
      super.setStep(IncoroporationFormStep.BASKET, direction);
      return;
    }
    super.setStep(step, direction);
  }

  get activeStep(): RegisterFormStep<IncoroporationFormStep> {
    const step = this.steps.activeStep;
    if (
      step.type === IncoroporationFormStep.BASKET &&
      this._basketQuestionsStore.activeQuestionKey === BasketQuestion.VirtualOfficeSelection
    ) {
      return new RegisterFormStep(step.type, "Company address", step.controller);
    }
    if (
      step.type === IncoroporationFormStep.BASKET &&
      this._basketQuestionsStore.activeQuestionKey === BasketQuestion.Checkout &&
      this._featureStore.hasFeature("formationOnePageCheckout")
    ) {
      return new RegisterFormStep(step.type, "Check out", step.controller);
    }
    return step;
  }
}
